/**
 * Amount Discounts Service
 * Handles quantity-based discounts from Firestore (not Promotions)
 * Display-only in Item Dialog for Phase 1
 */
import { doc, getDoc } from 'firebase/firestore'
import { getFirebaseDb } from '../firebase/firebase.js'

// In-memory cache: key = `${assortmentId}::${makat}`
const discountsCache = new Map()

/**
 * Determine which assortment ID to use for loading discounts
 * Rules:
 * - If no auth client => "10001"
 * - If client.merakez === "20000" => "10001" (special rule)
 * - Otherwise use client.merakez or fallback to "10001"
 * @param {Object|null} authClient - The auth.user.client object
 * @returns {string} Assortment ID
 */
export function getDiscountAssortmentId(authClient) {
  if (!authClient) return '10001'

  const merakez = authClient.merakez
  if (!merakez) return '10001'

  // Special rule: merakez "20000" loads from "10001"
  if (merakez === '20000') return '10001'
  return merakez
}

/**
 * Fetch amount discounts document from Firestore
 * Path: AmountDiscounts/{assortmentId}/discounts/{makat}
 * @param {Object} params
 * @param {string} params.assortmentId
 * @param {string} params.makat
 * @returns {Promise<Object|null>} Normalized discount data or null
 */
export async function fetchAmountDiscountsDoc({ assortmentId, makat }) {
  if (!assortmentId || !makat) return null

  // Ensure docId is always a string for Firestore
  const docId = String(makat)

  try {
    const db = getFirebaseDb()
    const docRef = doc(db, 'AmountDiscounts', String(assortmentId), 'discounts', docId)
    const docSnap = await getDoc(docRef)

    if (!docSnap.exists()) {
      return null
    }

    const data = docSnap.data()
    return normalizeDiscountsData(data, assortmentId, makat)
  } catch (err) {
    console.error('[AmountDiscounts] Failed to fetch:', err)
    return null
  }
}

/**
 * Normalize discounts data from Firestore
 * - Coerce minQty and discount to numbers
 * - Filter invalid: minQty <= 0, discount isNaN, discount <= 0
 * - Sort by minQty ascending
 * - Deduplicate by minQty (keep last)
 * @param {Object} data - Raw Firestore document data
 * @param {string} assortmentId
 * @param {string} makat
 * @returns {Object|null}
 */
function normalizeDiscountsData(data, assortmentId, makat) {
  if (!data) return null

  const rawDiscounts = data.discounts
  if (!Array.isArray(rawDiscounts) || rawDiscounts.length === 0) {
    return null
  }

  // Process and validate discounts
  const validDiscounts = []
  const seenMinQty = new Map()

  for (const item of rawDiscounts) {
    const minQty = Number(item.minQty)
    const discount = Number(item.discount)

    // Filter invalid entries
    if (isNaN(minQty) || minQty <= 0) continue
    if (isNaN(discount) || discount <= 0) continue

    // Keep last occurrence for deduplication
    seenMinQty.set(minQty, { minQty, discountPercent: discount })
  }

  // Convert map to array and sort by minQty
  for (const entry of seenMinQty.values()) {
    validDiscounts.push(entry)
  }
  validDiscounts.sort((a, b) => a.minQty - b.minQty)

  if (validDiscounts.length === 0) {
    return null
  }

  return {
    assortmentId,
    makat,
    discounts: validDiscounts,
  }
}

/**
 * Get amount discounts for an item with caching
 * @param {Object} params
 * @param {string} params.assortmentId
 * @param {string} params.makat
 * @returns {Promise<Object|null>}
 */
export async function getAmountDiscountsForItem({ assortmentId, makat }) {
  if (!assortmentId || !makat) return null

  const cacheKey = `${assortmentId}::${makat}`

  // Return cached if exists
  if (discountsCache.has(cacheKey)) {
    return discountsCache.get(cacheKey)
  }

  // Fetch, normalize, cache and return
  const result = await fetchAmountDiscountsDoc({ assortmentId, makat })
  discountsCache.set(cacheKey, result)

  return result
}

/**
 * Compute discount ladder for display
 * @param {Object} params
 * @param {number} params.baseUnitPrice - Item price (pre-VAT, pre-deposit)
 * @param {number} params.selectedQty - Current dialog qty
 * @param {Array} params.discounts - Array of { minQty, discountPercent }
 * @returns {Array} Ladder array with { minQty, discountPercent, unitPriceAfterDiscount, isActive }
 */
export function computeDiscountLadder({ baseUnitPrice, selectedQty, discounts }) {
  if (!Array.isArray(discounts) || discounts.length === 0) {
    return []
  }

  const price = Number(baseUnitPrice)
  if (isNaN(price) || price <= 0) {
    return []
  }

  const qty = Number(selectedQty) || 0

  // Find active tier: largest minQty <= selectedQty
  let activeTierMinQty = null
  for (const tier of discounts) {
    if (tier.minQty <= qty) {
      activeTierMinQty = tier.minQty
    }
  }

  // Build ladder array
  return discounts.map((tier) => {
    // tierUnitPrice = baseUnitPrice * (1 - discountPercent/100)
    let unitPriceAfterDiscount = price * (1 - tier.discountPercent / 100)
    unitPriceAfterDiscount = Math.max(0, unitPriceAfterDiscount)

    return {
      minQty: tier.minQty,
      discountPercent: tier.discountPercent,
      unitPriceAfterDiscount: unitPriceAfterDiscount.toFixed(2),
      isActive: tier.minQty === activeTierMinQty,
    }
  })
}

/**
 * Clear the discounts cache (for testing or manual refresh)
 */
export function clearDiscountsCache() {
  discountsCache.clear()
}
